import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useState, type JSX } from "react";

import { useCalendarLocation } from "../calendarLocation.js";
import { CalendarDayView, CalendarMonthView, CalendarOverview, CalendarWeekView } from "../components/CalendarViews.js";
import { EventDetails } from "../components/EventDetails.js";
import { EventForm, type EventDraft } from "../components/EventForm.js";
import { useCalendars, useCreateEvent, useDeleteEvent, useEvents, useRespondToEvent, useUpdateEvent } from "../query/calendarQueries.js";
import { calendarPeriodLabel, type CalendarView, type WeekRange } from "../weekRange.js";

const VIEWS: Array<{ id: CalendarView; label: string }> = [
  { id: "overview", label: "Übersicht" },
  { id: "day", label: "Tag" },
  { id: "week", label: "Woche" },
  { id: "month", label: "Monat" },
];

export function CalendarPage(): JSX.Element {
  const location = useCalendarLocation();
  const [formRange, setFormRange] = useState<{ start: Date; end: Date } | null>(null);
  const calendars = useCalendars();
  const events = useEvents(location.range.from.toISOString(), location.range.to.toISOString());
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();
  const respond = useRespondToEvent();
  const visible = (events.data ?? []).filter((entry) => !location.hidden.has(entry.calendarId));
  const selected = visible.find((event) => event.id === location.eventId) ?? null;
  const viewProps = {
    events: visible,
    anchor: location.anchor,
    days: location.range.days,
    onCreateRange: (start: Date, end: Date) => setFormRange({ start, end }),
    onChangeEvent: (eventId: string, startsAt: Date, endsAt: Date) => updateEvent.mutate({ eventId, body: { startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() } }),
    onSelect: location.showEvent,
    onPickDay: location.showDay,
  };

  const submit = (draft: EventDraft): void => {
    createEvent.mutate(draft, { onSuccess: () => setFormRange(null) });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="sticky top-0 z-20 shrink-0 border-b border-line bg-surface-raised px-3 py-2 sm:px-5">
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Voriger Zeitraum" onClick={() => location.move(-1)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-surface-sunken hover:text-ink"><ChevronLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1 text-center">
            <h2 className="truncate text-sm font-extrabold capitalize text-ink">{navigationTitle(location.view, location.anchor)}</h2>
            <p className="truncate text-[11px] font-semibold text-ink-muted">{navigationMeta(location.view, location.anchor, location.range)}</p>
          </div>
          <button type="button" aria-label="Nächster Zeitraum" onClick={() => location.move(1)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-surface-sunken hover:text-ink"><ChevronRight className="h-5 w-5" /></button>
          {!location.range.days.some((day) => day.toDateString() === new Date().toDateString()) ? (
            <button type="button" onClick={() => location.showDate(new Date())} className="shrink-0 rounded-full border border-line px-2.5 py-1.5 text-xs font-bold text-ink-muted">Heute</button>
          ) : null}
          <button type="button" aria-label="Termin erstellen" onClick={() => setFormRange(defaultRange(location.anchor))} className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand/10 text-brand"><Plus className="h-4 w-4" /></button>
        </div>

        <div role="tablist" aria-label="Kalenderansicht" className="mt-2 grid grid-cols-4 rounded-full bg-surface-sunken p-1">
          {VIEWS.map((view) => (
            <button key={view.id} type="button" role="tab" aria-selected={location.view === view.id} onClick={() => location.showView(view.id)} className={["min-w-0 rounded-full px-1 py-1.5 text-[13px] font-bold", location.view === view.id ? "bg-surface-raised text-brand shadow-sm" : "text-ink-muted hover:text-ink"].join(" ")}>
              {view.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto bg-surface-raised pt-2">
        {updateEvent.error ? <p role="alert" className="mx-3 mb-2 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">Der Termin konnte nicht verschoben werden.</p> : null}
        {events.isPending ? <p className="py-16 text-center text-sm text-ink-muted">Termine werden geladen …</p> : null}
        {events.error ? <p role="alert" className="text-sm text-danger">{events.error.message}</p> : null}
        {!events.isPending && !events.error && location.view === "overview" ? <CalendarOverview {...viewProps} /> : null}
        {!events.isPending && !events.error && location.view === "day" ? <CalendarDayView {...viewProps} /> : null}
        {!events.isPending && !events.error && location.view === "week" ? <CalendarWeekView {...viewProps} /> : null}
        {!events.isPending && !events.error && location.view === "month" ? <CalendarMonthView {...viewProps} /> : null}
      </div>

      {formRange ? <EventForm calendars={calendars.data ?? []} initialStart={formRange.start} initialEnd={formRange.end} busy={createEvent.isPending} error={createEvent.error?.message ?? null} onSubmit={submit} onCancel={() => setFormRange(null)} /> : null}
      {selected ? <EventDetails event={selected} busy={deleteEvent.isPending || respond.isPending} error={deleteEvent.error?.message ?? respond.error?.message ?? null} onClose={() => location.showEvent(null)} onDelete={() => deleteEvent.mutate(selected.id, { onSuccess: () => location.showEvent(null) })} onRespond={(status) => respond.mutate({ eventId: selected.id, status })} /> : null}
    </div>
  );
}

function defaultRange(day: Date): { start: Date; end: Date } {
  const start = new Date(day);
  start.setHours(9, 0, 0, 0);
  return { start, end: new Date(start.getTime() + 60 * 60_000) };
}

function navigationTitle(view: CalendarView, date: Date): string {
  if (view === "month") return date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  if (view === "week") return `KW ${weekNumber(date)}`;
  if (date.toDateString() === new Date().toDateString()) return "Heute";
  return date.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
}

function navigationMeta(view: CalendarView, date: Date, range: WeekRange): string {
  if (view === "overview") return "Nächste 30 Tage";
  if (view === "month") return "Monatsansicht";
  if (view === "day") return date.toLocaleDateString("de-DE", { year: "numeric" });
  return calendarPeriodLabel(view, date, range);
}

function weekNumber(date: Date): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}
